// The FX slot's laws (P4.8) — FR-17's "core per-deck FX", and the vocabulary both sides share.
//
// Here rather than in `/audio` for `mixer.ts`'s reason: **the UI needs half of this and the worklet
// needs the other half.** A division selector has to draw "1/2" and the audio thread has to turn
// that into a number of samples; a wet knob has to print a percentage and the strip has to apply a
// gain. One definition, two readers, and `ui-no-engine-internals` keeps neither dragging the other
// into its bundle.
//
// ## The delay time is musical, and that is the whole prompt
//
// P4.8's rule: *"The delay time is a musical division of the deck's beat, not a millisecond
// value."* So nothing here names a millisecond. A division is a fraction of a beat, and the beat
// arrives per block in `DspBlock.beatSeconds` — **already at the deck's current playback rate**,
// which is what makes the echo follow the pitch fader without anyone recomputing anything. §1 of
// the M4 pack is explicit that these times come from `platform/types/sync.ts` rather than a wall
// clock.

/**
 * FR-17's musical divisions, as fractions of one beat.
 *
 * The four P4.8 names. Fractions of a *beat* rather than of a bar, because `beatSeconds` is the
 * only period the grid gives us — `SyncGrid` is an anchor and a beat period, with no bar length in
 * it, so a bar-relative division would be inventing a time signature nobody measured.
 */
export const FX_DIVISION = {
  QUARTER: 0.25,
  HALF: 0.5,
  THREE_QUARTER: 0.75,
  WHOLE: 1,
} as const;

export type FxDivision = (typeof FX_DIVISION)[keyof typeof FX_DIVISION];

/** The order a selector should offer them in. Shortest first, which is how hardware lays them out. */
export const FX_DIVISIONS: readonly FxDivision[] = [
  FX_DIVISION.QUARTER,
  FX_DIVISION.HALF,
  FX_DIVISION.THREE_QUARTER,
  FX_DIVISION.WHOLE,
];

/** What a division is called on screen. */
export function fxDivisionLabel(division: FxDivision): string {
  switch (division) {
    case FX_DIVISION.QUARTER:
      return '1/4';
    case FX_DIVISION.HALF:
      return '1/2';
    case FX_DIVISION.THREE_QUARTER:
      return '3/4';
    default:
      return '1/1';
  }
}

/**
 * The nearest legal division to an arbitrary number, for the audio thread.
 *
 * ⚠️ **A `COMMAND_RECORD.VALUE` is an f32 off a shared buffer**, so the worklet must treat it as
 * arbitrary rather than as one of four values it trusts. Nearest rather than a reject, because a
 * division that failed to apply would be a control that silently did nothing — and unlike a gain
 * there is no safe "ignore it" state for a delay time that is already running.
 */
export function fxDivisionFromValue(value: number): FxDivision {
  if (!Number.isFinite(value)) {
    return FX_DIVISION.HALF;
  }
  let best: FxDivision = FX_DIVISIONS[0] ?? FX_DIVISION.QUARTER;
  let bestDistance = Infinity;
  for (const division of FX_DIVISIONS) {
    const distance = Math.abs(division - value);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = division;
    }
  }
  return best;
}

/**
 * How the echo timed itself for the block just processed (P4.8).
 *
 * ⚠️ **`free` is a reading, not a failure.** P4.8's rule: *"A track with no grid must still echo,
 * unquantized and honestly so, exactly as FR-04's loops do — the pattern is established and
 * re-deciding it would produce two answers to one question."* `LoopQuantization` in `loop.ts` is
 * that established pattern, and this is deliberately **not** a copy of it: a loop distinguishes
 * `unquantized-no-grid` from `unquantized-low-confidence` because the deck knows the tempo
 * confidence, and the mixer does not.
 *
 * So the kernel reports what it *did* — synced to a beat, or running free — and the reason it had
 * to stays with the deck, which is the side that owns it. Re-deriving a confidence threshold in the
 * mixer would be exactly the second answer the rule warns about. See BACKLOG-NOTES under P4.8 for
 * the follow-up: the deck should publish its quantization verdict rather than its raw grid.
 */
export type EchoTiming = 'beat' | 'free';

/**
 * The delay to use when there is no usable beat, in seconds.
 *
 * A musical-ish 375 ms — a 1/2-beat division at 80 BPM — rather than a round 500 ms, so a gridless
 * track echoes at a rate that sits inside dance tempo rather than against it. Free-running, and the
 * unit says so; the number only has to be defensible, not correct, because there is nothing here to
 * be correct *against*.
 */
export const ECHO_FREE_DELAY_SECONDS = 0.375;

/**
 * The longest delay the line must hold, in seconds — this sizes the buffer, once, at construction.
 *
 * The worst case the transport allows: the largest division is one whole beat, and
 * `DspBlock.beatSeconds` is the period **at the sounding rate**, so a deck pitched down stretches
 * it. 2.5 s is one beat at 24 BPM sounding — a 48 BPM track at half speed, or a 72 BPM track at a
 * third — which is past anything the pitch fader and a real record can produce together.
 *
 * The cost is stated rather than hidden: at 48 kHz that is 120,000 floats, 480 kB per channel, so
 * **1.9 MB for two stereo strips**. Against NFR-P5's 1.2 GB ceiling with two tracks loaded that is
 * rounding error, and it is the price of never allocating on the audio thread — a delay line that
 * grew when the tempo dropped would allocate in `process`, which the kernel contract forbids
 * outright.
 */
export const ECHO_MAX_DELAY_SECONDS = 2.5;

/**
 * The most feedback a knob can ask for.
 *
 * ⚠️ **Strictly below 1, and that is a stability bound rather than taste.** A delay line whose
 * feedback is 1 never decays and one above 1 diverges — on the audio thread, into the limiter, and
 * then into a listener's ears. 0.85 leaves a long musical tail (about fourteen repeats before the
 * echo is 20 dB down) while guaranteeing the loop is a contraction.
 */
export const ECHO_FEEDBACK_MAX = 0.85;

/** A feedback knob's 0…1 position to the coefficient it means. Linear: this is a ratio already. */
export function echoFeedbackFromPosition(position: number): number {
  if (!Number.isFinite(position)) {
    return 0;
  }
  return Math.min(1, Math.max(0, position)) * ECHO_FEEDBACK_MAX;
}

/**
 * A wet/dry knob's 0…1 position to the pair of gains it means.
 *
 * **Constant power, for P4.5's crossfader reason.** An echo's repeats are decorrelated from the dry
 * signal — they are the same sound at a different time — so the two sum in power rather than in
 * amplitude. A linear blend would therefore dip about 3 dB at the midpoint, which is exactly the
 * hole `crossfaderGains` exists to remove, and it would be more noticeable here because a hand
 * riding an FX knob passes through the middle every time.
 *
 * Exact at both ends by construction rather than by rounding: P4.5 shipped a fader that printed
 * "−324.3 dB" for a channel the user had closed, because `Math.cos(Math.PI / 2)` is 6.12e-17.
 */
export function fxMixGains(position: number): readonly [dry: number, wet: number] {
  if (!Number.isFinite(position) || position <= 0) {
    return [1, 0];
  }
  if (position >= 1) {
    return [0, 1];
  }
  const angle = (position * Math.PI) / 2;
  return [Math.cos(angle), Math.sin(angle)];
}

// ---------------------------------------------------------------------------
// FR-17's reverb (P4.9)
// ---------------------------------------------------------------------------

/**
 * The shortest and longest tail the decay knob can ask for, in seconds (RT60).
 *
 * 0.2 s is a tiled room — short enough to read as ambience rather than as an effect. 8 s is a large
 * hall and past anything a mix survives, which is the point of a maximum: it is the end of the
 * control's travel, not a recommendation.
 *
 * ⚠️ **Both ends are finite and the bottom is not zero.** A decay of 0 would make every line's
 * feedback gain 0 and the "reverb" a single delayed copy; a decay of infinity is a tank that never
 * loses energy, which on the audio thread is a tone that grows until the limiter holds it down.
 * `PlateReverb.setDecaySeconds` derives a per-line gain strictly below 1 from whatever lands here.
 */
export const REVERB_MIN_DECAY_SECONDS = 0.2;
export const REVERB_MAX_DECAY_SECONDS = 8;

/**
 * The damping filter's corner at each end of its knob, in Hz.
 *
 * At the bright end (16 kHz) the feedback path is essentially unfiltered and the tail keeps its
 * treble — a plate. At the dark end (800 Hz) the highs are gone within the first few passes — a room
 * full of soft furniture. The knob moves logarithmically between them, because pitch does.
 */
export const REVERB_DAMPING_MAX_HZ = 16_000;
export const REVERB_DAMPING_MIN_HZ = 800;

/** A decay knob's 0…1 position to the RT60 it means. Logarithmic: the ear hears decay time in
 * ratios, so a linear knob would spend most of its travel between 4 and 8 seconds. */
export function reverbDecayFromPosition(position: number): number {
  if (!Number.isFinite(position)) {
    return REVERB_MIN_DECAY_SECONDS;
  }
  const clamped = Math.min(1, Math.max(0, position));
  return (
    REVERB_MIN_DECAY_SECONDS *
    Math.pow(REVERB_MAX_DECAY_SECONDS / REVERB_MIN_DECAY_SECONDS, clamped)
  );
}

/**
 * Which FX unit a slot holds. `NONE` is a slot that is switched off, not an empty one.
 *
 * Numeric because it crosses the command ring as an f32 — the same arrangement
 * `CROSSFADER_CURVE_CODE` uses. ⚠️ **Never renumber in place**: the two sides ship in bundles that
 * cache separately, and a stale worklet reading a reused code would load the wrong unit silently.
 */
export const FX_UNIT = {
  NONE: 0,
  ECHO: 1,
  /** P4.9. Declared here so the slot's switch is exhaustive before the unit exists. */
  REVERB: 2,
} as const;

export type FxUnitKind = (typeof FX_UNIT)[keyof typeof FX_UNIT];

/** Anything unrecognised is `NONE` — the only value that cannot make a noise nobody asked for. */
export function fxUnitFromCode(code: number): FxUnitKind {
  return code === FX_UNIT.ECHO
    ? FX_UNIT.ECHO
    : code === FX_UNIT.REVERB
      ? FX_UNIT.REVERB
      : FX_UNIT.NONE;
}

/**
 * How many FX slots each channel strip carries.
 *
 * **Two, because FR-17 names two core units and both are per-deck** — "beat-synced echo and
 * reverb". One slot would make them alternatives rather than a chain, which is a product decision
 * FR-17 did not make.
 *
 * ⚠️ ADR-L13's test applies and this passes it where a third channel strip did not: an unloaded
 * slot costs **one comparison per block**, not an EQ and a filter running over silence for two
 * milestones. Adding a third slot is a constant here and nothing else.
 */
export const FX_SLOTS_PER_STRIP = 2;

/**
 * Packs a strip and a slot into one `COMMAND_RECORD.TARGET` u16 — `packEqTarget`'s arrangement.
 *
 * ⚠️ **Unpacked by two number-returning functions rather than one that returns `{ strip, slot }`,
 * which is where this differs from `unpackEqTarget` deliberately.** That one builds an object, and
 * P4.6 spent four bugs on allocation in this neighbourhood. Commands are bursty rather than
 * per-block so the cost is small either way; the point is that the audio thread should not have a
 * single allocating call site whose only defence is "it is not hit very often".
 */
export function packFxTarget(strip: number, slot: number): number {
  return ((slot & 0xff) << 8) | (strip & 0xff);
}

export function fxTargetStrip(target: number): number {
  return target & 0xff;
}

export function fxTargetSlot(target: number): number {
  return (target >>> 8) & 0xff;
}
